import { useIsAdhaJourney } from '@/core/hooks';
import { useAppStore } from '@/core/stores/appStore';
import { useQuery } from '@tanstack/react-query';
import { Platform } from 'react-native';
import {
  mapProfileDetailsToUserPatch,
  profileService,
} from '@/modules/auth/services/profileService';
import {
  fetchServiceRequestCategories,
  fetchServiceRequests,
  ServiceRequestCategory,
  ServiceRequestDto,
} from '../services/serviceRequestService';

function readCustomerId(raw: unknown): string | null {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/**
 * ADHA / Emirates ID: customer_id is required as `?customer_id=`.
 * After refresh, re-fetch profile-details if the store has no id yet.
 */
async function resolveCustomerIdForServiceRequests(
  shouldResolveFromProfile: boolean,
): Promise<string | null> {
  const fromStore = readCustomerId(
    useAppStore.getState().auth.user?.customer_id,
  );
  if (fromStore) {
    return fromStore;
  }

  if (!shouldResolveFromProfile) {
    return null;
  }

  const details = await profileService.getProfileDetails();
  const patch = mapProfileDetailsToUserPatch(details);
  useAppStore.getState().setProfile(patch);

  return (
    readCustomerId(useAppStore.getState().auth.user?.customer_id) ||
    readCustomerId(details.customer_id)
  );
}

export function useServiceRequests() {
  const isAdhaJourney = useIsAdhaJourney();
  const isAuthenticated = useAppStore((s) => s.auth.isAuthenticated);
  const customerId = useAppStore((s) =>
    readCustomerId(s.auth.user?.customer_id),
  );
  const loginMethod = useAppStore((s) => s.auth.loginMethod);

  // Mobile: ADHA / Emirates ID require customer_id. Web: send when available.
  const requiresCustomerId =
    isAdhaJourney ||
    loginMethod === 'adha' ||
    loginMethod === 'emirates_id';

  return useQuery<ServiceRequestDto[]>({
    queryKey: [
      'service-requests',
      requiresCustomerId || Platform.OS === 'web' ? customerId : null,
      requiresCustomerId,
    ],
    queryFn: async () => {
      const resolvedId = await resolveCustomerIdForServiceRequests(
        requiresCustomerId,
      );

      if (requiresCustomerId && !isAdhaJourney && !resolvedId) {
        throw new Error(
          'Missing customer_id — service requests require customer_id',
        );
      }

      const sendCustomerIdWhenNotAdha =
        Platform.OS === 'web'
          ? Boolean(resolvedId)
          : requiresCustomerId && Boolean(resolvedId);
      const shouldSendCustomerId = isAdhaJourney ? false : sendCustomerIdWhenNotAdha;

      return fetchServiceRequests(shouldSendCustomerId ? resolvedId : null);
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });
}

export function useServiceRequestCategories() {
  const isAdhaJourney = useIsAdhaJourney();
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
  return useQuery<ServiceRequestCategory[]>({
    queryKey: ['service-request-categories', isAuthenticated, isAdhaJourney],
    queryFn: fetchServiceRequestCategories,
    staleTime: 10 * 60 * 1000,
  });
}
